/**
 * CoachAI Icon System — v2 (2026-05-20)
 *
 * Eigenständig gezeichnete SVG-Icons, kein Lucide/Feather/Heroicons-Fork.
 * Design-Philosophie passt zur Marke (Athletic Lime + Charcoal):
 *
 *   - Outline-only (kein Fill-Cheating, kein Opacity-Trick).
 *   - Einheitliche Stroke-Stärke 2 (kräftiger als Feather-Standard,
 *     liest sich auf Light- und Dark-Backgrounds gleichermaßen gut).
 *   - Round Linecap + Linejoin (refined, nicht aggressiv).
 *   - ViewBox immer 24×24, jedes Icon visuell auf gemeinsamem Grid.
 *   - color via currentColor — Icons erben die Farbe vom Container.
 *
 * Diese Disziplin bedeutet: jedes Icon im System fühlt sich verwandt an,
 * keine optischen Brüche zwischen filled/outline oder dünn/dick.
 *
 * Brand-Mark (IconFlame): asymmetrische 2-Pfad-Flamme mit innerem
 * Ember. Geometrisch, nicht illustrativ — passt zum Performance-Vibe
 * der Landing/B2B-Page.
 */

import React from 'react';

// ─── Helper ──────────────────────────────────────────────
// `d` ist der Pfad-Content. `size`/`color`/`sw` Defaults für visuelle
// Konsistenz. Override pro Icon möglich, wo nötig (z.B. IconCheck
// braucht etwas mehr Stroke bei kleinen Größen).

const I = ({ d, size = 22, color = 'currentColor', sw = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {d}
  </svg>
);

// ═════════════════════════════════════════════════════════
// BRAND MARK
// ═════════════════════════════════════════════════════════

/**
 * IconFlame — der CoachAI-Brand-Mark.
 *
 * Asymmetrische Flamme (Peak leicht nach links versetzt = dynamische
 * Bewegung statt statische Symmetrie), innen ein kleinerer Ember.
 * Beide Pfade stroke-only — keine Gradient-Cheats, keine Opacity-
 * Tricks. Liest sich identisch in Lime auf Dark und Charcoal auf
 * Lime.
 */
export const IconFlame = ({ size = 24, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Outer flame — asymmetric, leicht nach links lehnend */}
    <path d="M11 3 C 9 7, 6.5 10, 6.5 14.5 C 6.5 18.5, 9 21, 12 21 C 15 21, 17.5 18.5, 17.5 14.5 C 17.5 11, 15.5 9, 14 5.5" />
    {/* Inner ember — kleinere Flamme im Kern */}
    <path d="M12 13 C 11 14.5, 10.5 15.5, 10.5 16.5 C 10.5 17.5, 11.2 18, 12 18 C 12.8 18, 13.5 17.5, 13.5 16.5 C 13.5 15.5, 13 14.5, 12 13" />
  </svg>
);

// ═════════════════════════════════════════════════════════
// NAVIGATION (Bottom-Nav in der App)
// ═════════════════════════════════════════════════════════

/** IconHome — Haus mit Tür. Geometrisch sauber, kein Schnörkel. */
export const IconHome = ({ size, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M3 11 L 12 3 L 21 11 V 20 a 1 1 0 0 1 -1 1 h-5 V 14 h-4 V 21 H 4 a 1 1 0 0 1 -1 -1 Z" />
      </>
    }
  />
);

/** IconFork — Gabel + Messer als parallele Striche, Kopf abgerundet. */
export const IconFork = ({ size, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        {/* Gabel links */}
        <path d="M6 3 V 8 a 2 2 0 0 0 4 0 V 3" />
        <path d="M8 8 V 21" />
        {/* Messer rechts */}
        <path d="M16 3 C 18 5, 18 9, 16 11" />
        <path d="M16 11 V 21" />
      </>
    }
  />
);

/** IconCamera — Body + Lens, Shutter-Mark im Inneren des Lens-Kreises. */
export const IconCamera = ({ size, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M3 8 H 7 L 9 5 H 15 L 17 8 H 21 a 1 1 0 0 1 1 1 V 19 a 1 1 0 0 1 -1 1 H 3 a 1 1 0 0 1 -1 -1 V 9 a 1 1 0 0 1 1 -1 Z" />
        <circle cx="12" cy="13" r="4" />
        {/* Shutter dot — subtle, gibt Lens-Tiefe */}
        <circle cx="12" cy="13" r="1.2" />
      </>
    }
  />
);

/** IconDumbbell — Hantel mit Endkappen, geometrisch reduziert. */
export const IconDumbbell = ({ size, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        {/* Linker Block */}
        <path d="M3 10 V 14" />
        <path d="M5 7 V 17" />
        <path d="M7 9 V 15" />
        {/* Stange */}
        <path d="M7 12 H 17" />
        {/* Rechter Block */}
        <path d="M17 9 V 15" />
        <path d="M19 7 V 17" />
        <path d="M21 10 V 14" />
      </>
    }
  />
);

/** IconSettings — Zahnrad geometrisch (8 Speichen), Center-Kreis. */
export const IconSettings = ({ size, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2 V 5" />
        <path d="M12 19 V 22" />
        <path d="M22 12 H 19" />
        <path d="M5 12 H 2" />
        <path d="M19.07 4.93 L 16.95 7.05" />
        <path d="M7.05 16.95 L 4.93 19.07" />
        <path d="M19.07 19.07 L 16.95 16.95" />
        <path d="M7.05 7.05 L 4.93 4.93" />
      </>
    }
  />
);

// ═════════════════════════════════════════════════════════
// FEATURE-ICONS (Section-Marker, Landing, App-Cards)
// ═════════════════════════════════════════════════════════

/** IconDroplet — Tropfen-Outline, kein Fill. */
export const IconDroplet = ({ size = 22, color = 'currentColor' }) => (
  <I
    size={size}
    color={color}
    d={
      <path d="M12 3 C 10 6, 6 10, 6 14 a 6 6 0 0 0 12 0 C 18 10, 14 6, 12 3 Z" />
    }
  />
);

/** IconScale — Waage mit horizontaler Balken-Linie + Standsymbol. */
export const IconScale = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <path d="M7 10 H 17" />
        <path d="M12 10 V 16" />
        <circle cx="12" cy="14" r="0.8" fill={color || 'currentColor'} stroke="none" />
      </>
    }
  />
);

/** IconTarget — drei konzentrische Kreise, geometrisch sauber. */
export const IconTarget = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1.2" fill={color || 'currentColor'} stroke="none" />
      </>
    }
  />
);

/** IconClock — Kreis + Stunden/Minutenzeiger. */
export const IconClock = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7 V 12 L 15.5 14" />
      </>
    }
  />
);

/** IconZap — Blitz outline-only, asymmetrisch (passt zu IconFlame). */
export const IconZap = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <path d="M13 3 L 4 13 H 11 L 10 21 L 20 11 H 13 L 14 3 Z" />
    }
  />
);

/** IconCheck — abgerundeter Häkchen, etwas stärkere Stroke für klein. */
export const IconCheck = ({ size = 14, color = 'currentColor' }) => (
  <I
    size={size}
    color={color}
    sw={2.4}
    d={<path d="M4 12 L 9 17 L 20 6" />}
  />
);

/** IconX — Kreuz für Schließen/Abbrechen. */
export const IconX = ({ size = 14, color }) => (
  <I
    size={size}
    color={color}
    sw={2.4}
    d={
      <>
        <path d="M6 6 L 18 18" />
        <path d="M18 6 L 6 18" />
      </>
    }
  />
);

/** IconChevronLeft — Zurück-Pfeil. */
export const IconChevronLeft = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    sw={2.4}
    d={<path d="M15 6 L 9 12 L 15 18" />}
  />
);

/** IconCalendar — Kalender-Block mit Header-Linie und 2 Ringen. */
export const IconCalendar = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10 H 21" />
        <path d="M8 3 V 7" />
        <path d="M16 3 V 7" />
      </>
    }
  />
);

/** IconMicroscope — vereinfacht: Tubus + Tisch. */
export const IconMicroscope = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M14 3 V 9 a 2 2 0 0 0 4 0 V 3 Z" />
        <path d="M16 11 L 11 19" />
        <circle cx="9" cy="20" r="2" />
        <path d="M5 20 H 19" />
      </>
    }
  />
);

/** IconWarning — Dreieck mit Ausrufezeichen, semantic-Farbe meist warning. */
export const IconWarning = ({ size = 22, color = 'currentColor' }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M12 3 L 22 20 H 2 Z" />
        <path d="M12 10 V 14" />
        <path d="M12 17.5 V 17.6" />
      </>
    }
  />
);

/** IconTrash — Mülleimer mit Henkel. */
export const IconTrash = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M3 6 H 21" />
        <path d="M5 6 V 20 a 2 2 0 0 0 2 2 H 17 a 2 2 0 0 0 2 -2 V 6" />
        <path d="M9 6 V 4 a 2 2 0 0 1 2 -2 H 13 a 2 2 0 0 1 2 2 V 6" />
      </>
    }
  />
);

/** IconSave — Disketten-Form (Floppy), geometrisch. */
export const IconSave = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M5 3 H 16 L 21 8 V 20 a 1 1 0 0 1 -1 1 H 4 a 1 1 0 0 1 -1 -1 V 4 a 1 1 0 0 1 1 -1 Z" />
        <path d="M7 21 V 14 H 17 V 21" />
        <path d="M7 3 V 8 H 14" />
      </>
    }
  />
);

/** IconKey — Schlüssel mit Bart. Steht für BYOK / Lizenz. */
export const IconKey = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="8" cy="15" r="4" />
        <path d="M11 12 L 22 1" />
        <path d="M16 7 L 19 10" />
        <path d="M19 4 L 22 7" />
      </>
    }
  />
);

/** IconInfo — Kreis mit „i". */
export const IconInfo = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11 V 17" />
        <path d="M12 8 V 8.1" />
      </>
    }
  />
);

/**
 * IconLotus — 3-Blatt-Lotus (Mitte + 2 Seitenblätter).
 * Steht für Ruhetag, Recovery, On-device-Calm.
 */
export const IconLotus = ({ size = 22, color = 'currentColor' }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        {/* Mittleres Blatt */}
        <path d="M12 4 C 10 7, 9 10, 9 13 C 9 16, 10.5 18, 12 18 C 13.5 18, 15 16, 15 13 C 15 10, 14 7, 12 4 Z" />
        {/* Linkes Blatt */}
        <path d="M5 11 C 5 13, 6 16, 9 18 C 9.5 17, 9 14, 7.5 11.5 C 7 10.5, 5.5 10, 5 11 Z" />
        {/* Rechtes Blatt */}
        <path d="M19 11 C 19 13, 18 16, 15 18 C 14.5 17, 15 14, 16.5 11.5 C 17 10.5, 18.5 10, 19 11 Z" />
      </>
    }
  />
);

/** IconMeal — Teller mit Henkel/Wölbung (vereinfacht). */
export const IconMeal = ({ size = 22, color = 'currentColor' }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <ellipse cx="12" cy="16" rx="9" ry="3.5" />
        <path d="M3 16 V 9 C 3 11, 7 12.5, 12 12.5 S 21 11, 21 9 V 16" />
        <path d="M8 7 C 8 8, 10 9, 12 9 S 16 8, 16 7" />
      </>
    }
  />
);

/** IconBolt — Blitz outline-only, identisch zu IconZap aber default warning-color. */
export const IconBolt = ({ size = 22, color = 'currentColor' }) => (
  <I
    size={size}
    color={color}
    d={
      <path d="M13 3 L 4 13 H 11 L 10 21 L 20 11 H 13 L 14 3 Z" />
    }
  />
);

// ═════════════════════════════════════════════════════════
// PERSONEN + RECOVERY (Phase 8.4)
// ═════════════════════════════════════════════════════════

/** IconUser — Kopf + Schulterkurve, simple Avatar-Form. */
export const IconUser = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21 V 20 a 6 6 0 0 1 6 -6 h 4 a 6 6 0 0 1 6 6 v 1" />
      </>
    }
  />
);

/** IconMoon — Sichel, geometrisch sauber. */
export const IconMoon = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <path d="M20 14 a 8 8 0 1 1 -10 -10 a 6 6 0 0 0 10 10 Z" />
    }
  />
);

/**
 * IconWalk — Gehende Figur (Kopf + Beine in Schritt-Position).
 * Reduziert auf das Wesentliche, kein Realismus.
 */
export const IconWalk = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <circle cx="13" cy="4" r="2" />
        {/* Körper */}
        <path d="M13 8 L 10 13 L 7 22" />
        {/* Hinteres Bein */}
        <path d="M10 13 L 14 16 L 15 22" />
        {/* Vorderer Arm */}
        <path d="M13 9 L 17 11" />
      </>
    }
  />
);

// ═════════════════════════════════════════════════════════
// NEU (2026-05-20) — für Marketing-Story-Telling
// ═════════════════════════════════════════════════════════

/**
 * IconSparkle — 4-Punkt-Stern mit kleineren Akzent-Punkten.
 * AI-Universal-Glyph (OpenAI / Linear Asks / etc.).
 * Passt für KI-Coach, AI-Analyse-Hinweise.
 */
export const IconSparkle = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        {/* Großer Stern in der Mitte */}
        <path d="M12 4 L 13.5 10.5 L 20 12 L 13.5 13.5 L 12 20 L 10.5 13.5 L 4 12 L 10.5 10.5 Z" />
        {/* Kleiner Akzent-Stern oben rechts */}
        <path d="M18 4 L 18.5 6 L 20.5 6.5 L 18.5 7 L 18 9 L 17.5 7 L 15.5 6.5 L 17.5 6 Z" />
      </>
    }
  />
);

/**
 * IconShield — Schild-Outline mit Checkmark.
 * Privacy / Sicherheit / Trust.
 */
export const IconShield = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        <path d="M12 2 L 4 6 V 12 C 4 17, 8 21, 12 22 C 16 21, 20 17, 20 12 V 6 Z" />
        <path d="M9 12 L 11 14 L 15 10" />
      </>
    }
  />
);

/**
 * IconArrowUp — bold up-arrow.
 * Steht für Progress / Results / Performance.
 */
export const IconArrowUp = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    sw={2.4}
    d={
      <>
        <path d="M12 19 V 5" />
        <path d="M5 12 L 12 5 L 19 12" />
      </>
    }
  />
);

/**
 * IconLayers — Drei gestackte Layers (perspektivisch).
 * Steht für Framework / Stack / Plattform — B2B-spezifisch.
 */
export const IconLayers = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    d={
      <>
        {/* Oberste Schicht (geschlossener Rhombus) */}
        <path d="M12 3 L 21 8 L 12 13 L 3 8 Z" />
        {/* Mittlere Schicht */}
        <path d="M3 12 L 12 17 L 21 12" />
        {/* Untere Schicht */}
        <path d="M3 16 L 12 21 L 21 16" />
      </>
    }
  />
);

/**
 * IconRing — Sauberer offener Kreis.
 * Alternative Brand-Mark-Variante; passt zur Macro-Ring-Visualisierung.
 */
export const IconRing = ({ size = 22, color }) => (
  <I
    size={size}
    color={color}
    sw={2.4}
    d={
      <>
        <path d="M21 12 A 9 9 0 1 0 17 18.5" />
        <circle cx="17" cy="18.5" r="1.6" fill={color || 'currentColor'} stroke="none" />
      </>
    }
  />
);
